/**
 * nxTree — 다단계 트리(카테고리 · 문서 목차)의 화면 동작.
 *
 * 원본(jQuery Fancytree + nx_exetree 25종)에서 무엇이 사라졌나
 * ---------------------------------------------------------
 *   * `key`(Fancytree 임시 id) → 없다. UUID 하나로 끝나므로
 *     `_by_nodekey` / `_by_pageid` 이중 명령이 통째로 사라졌다.
 *   * `get_source` · `get_savetreesrc` → 없다. 트리 전체를 stringify 해서
 *     파일 하나로 저장하던 구조가 DB 행 단위 UPDATE 로 바뀌었다.
 *   * `destroy` · `set_disable` → 없다. 서버가 그리므로 파괴할 위젯이 없다.
 *
 * 여기 있는 것
 * -----------
 *   상태     active() · byId() · children()
 *   펼침     toggle() · expandAll()      ← localStorage 에만 저장한다
 *   변경     add() · update() · move() · shift() · remove()  ← 전부 서버 왕복
 *   검색     search() · clearSearch()
 *
 * 펼침 상태를 왜 localStorage 에 두나
 * --------------------------------
 * README 12절이 "서버가 알 필요 없는 UI 상태(사이드바 접힘·목록 정렬·필터
 * 프리셋)" 를 localStorage 허용 범위로 명시한다. 트리 펼침이 정확히 거기다.
 * 원본도 Fancytree persist 확장으로 같은 곳에 뒀다(store:'local').
 * 서버 데이터에 섞지 않으므로, 저장 직전에 `"expanded":true` 를 false 로
 * 치환하던 원본의 꼼수도 필요 없다.
 */

const API = '/api/categories';

export interface TreeNode {
  id: string;
  parentId: string | null;
  depth: number;
  isGroup: boolean;
  name: string;
}

interface TreeState {
  root: HTMLElement;
  maxDepth: number;
  storeKey: string;
  /**
   * 이 트리의 검색 세대. 응답이 왔을 때 값이 바뀌어 있으면 늦게 온 것이라 버린다.
   *
   * ★ 트리마다 따로 센다. 전역 하나로 두면 에디터 화면(카테고리 트리 +
   *   문서 목차 트리)에서 한쪽 검색이 다른 쪽의 진행 중인 검색을 무효로 만든다.
   */
  searchSeq: number;
  /** 이 트리의 툴박스 팝업 id. `_tree.html` 의 toolbox(id=…) 와 짝이다. */
  toolId: string;
}

const states = new Map<string, TreeState>();
let activeId: string | null = null;

/**
 * 지금 화면에 **살아 있는** 트리만.
 *
 * ★ 7차 검수 — `states` 에서 지워지는 것이 하나도 없었다. 팝업 안의 선택
 *   트리(`nxtree-pick`)는 열 때 등록되고, 팝업이 닫히면 DOM 은 사라지는데
 *   **항목은 남았다.** 그래서 본 화면에서 [모두 접기] 를 누르면 그 죽은 트리에도
 *   "전부 접힘" 이 localStorage 에 저장되어, 다음에 [상위 변경] 을 열면
 *   **통째로 접힌 채** 떴다(그 팝업에는 펼치기 버튼이 없다 — 손으로 하나씩
 *   눌러 내려가야 한다).
 *   `active()`/`children()` 이 쓰던 `states.values().next().value` 도
 *   죽은 트리를 집을 수 있었다.
 */
function liveStates(): TreeState[] {
  const out: TreeState[] = [];
  for (const [key, st] of states) {
    if (st.root.isConnected) out.push(st);
    else states.delete(key); // 여기서 청소한다 — 따로 지우는 코드를 두면 잊는다
  }
  return out;
}

/**
 * 트리 하나. `id` 를 주면 그 트리, 안 주면 살아 있는 것 중 첫 번째.
 *
 * ★ 에디터 화면에는 트리가 **둘** 뜬다(카테고리 + 문서 목차). "첫 번째" 로
 *   고르면 목차 검색창에 친 글자가 카테고리 트리를 거르는 식으로 어긋난다.
 *   부르는 쪽이 아는 id 는 반드시 넘긴다 — 검색줄은 `data-tree` 로 알고 있다.
 */
function liveState(id?: string): TreeState | undefined {
  const live = liveStates();
  if (!id) return live[0];
  return live.find((st) => st.root.id === id);
}



// ---------------------------------------------------------------- 유틸
function nodeEl(root: HTMLElement, id: string): HTMLElement | null {
  return root.querySelector<HTMLElement>(`.nx-tree-node[data-id="${CSS.escape(id)}"]`);
}

function readNode(li: HTMLElement): TreeNode {
  return {
    id: li.dataset.id || '',
    parentId: li.dataset.parent || null,
    depth: Number(li.dataset.depth || 0),
    isGroup: li.dataset.group === '1',
    name: li.querySelector('.nx-tree-title')?.textContent?.trim() || '',
  };
}

/**
 * 서버가 돌려준 i18n 키를 사람이 읽을 문구로.
 *
 * 사전은 `app.ts` 의 `nx.t` — `partials/_i18n.html` 이 내려보낸 JSON 을 읽는다.
 * `nx` 가 아직 없을 수도 있으므로(부팅 순서) 매번 확인한다.
 */
function errText(
  detail: unknown,
  params?: Record<string, unknown>,
  st?: TreeState,
): string {
  const key = typeof detail === 'string' ? detail : 'tree.error.not_found';
  const t = window.nx?.t;
  // `tree.error.max_depth` 는 "최대 {max}단계" 라 값이 필요하다.
  // 서버는 키만 돌려주므로 화면에서 채운다.
  //
  // ★ 어느 트리에서 난 오류인지 **모르면** 문서의 첫 트리를 집는다. 화면에
  //   트리가 둘이면(에디터: 카테고리 4단계 + 목차 6단계) 그때부터 숫자가
  //   틀린다 — "최대 4단계입니다" 라고 하는데 실제 상한은 6인 식이다.
  //   그래서 아는 쪽은 `st` 를 넘긴다.
  const max =
    st?.maxDepth ??
    document.querySelector<HTMLElement>('.nx-tree[data-max-depth]')?.dataset.maxDepth ??
    '';
  const filled = { max, ...(params || {}) };
  return t ? t(key, filled) : key;
}

/** 서버가 돌려준 JSON. 키는 호출한 쪽이 안다 — 값은 `unknown` 으로 받아 캐스팅한다. */
type ApiJson = Record<string, unknown> & { detail?: unknown };

/** `call()` 이 던지는 오류. `status`/`key` 를 붙여 호출한 쪽이 구분할 수 있게 한다. */
interface ApiError extends Error {
  key?: unknown;
  status?: number;
}

/**
 * `call()` 이 오류 문구를 만들 때 참고할 트리.
 *
 * ★ 8차 검수 — `errText(detail, params, st)` 로 인자는 뚫어 놨는데 **아무도
 *   넘기지 않아** 그 수정이 통째로 죽어 있었다. 호출 지점마다 트리를 인자로
 *   들고 다니면 `tree.add()` 같은 공개 API 의 모양이 바뀌므로, 요청 직전에
 *   여기에 적어 둔다(요청은 겹치지 않는다 — 한 번에 하나씩 눌린다).
 */
let errCtx: TreeState | undefined;

async function call(url: string, init?: RequestInit): Promise<ApiJson> {
  const method = (init?.method || 'GET').toUpperCase();
  const res = await fetch(url, {
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      // ★ CSRF — 본문이 JSON 이라 서버가 본문을 뒤지지 않는다. 헤더로 보낸다.
      //   GET 에는 붙이지 않는다(검사 대상이 아니고, 로그에 남길 이유도 없다).
      ...(method === 'GET' ? {} : (window.nx?.csrfHeaders?.() ?? {})),
    },
    ...init,
  });
  let data: ApiJson | null = null;
  try {
    data = (await res.json()) as ApiJson;
  } catch {
    /* 본문이 없을 수 있다 */
  }
  if (!res.ok) {
    const err = new Error(errText(data?.detail, undefined, errCtx)) as ApiError;
    err.key = data?.detail;
    err.status = res.status;
    throw err;
  }
  return data ?? {};
}

// ---------------------------------------------------------------- 펼침
/**
 * 저장된 펼침 상태. **아직 저장한 적이 없으면 `null`** 이다.
 *
 * ★ "저장된 적 없음" 과 "전부 접었음(빈 목록)" 을 반드시 구분해야 한다.
 *   예전에는 둘 다 빈 Set 이라, **전부 접고 새로고침하면 다시 다 펼쳐졌다.**
 *   (`applyExpanded` 가 `size === 0` 을 "저장된 게 없다" 로 읽었다)
 *   최상위가 하나뿐인 트리에서는 그 하나만 접어도 바로 이 상태가 된다.
 */
function loadExpanded(st: TreeState): Set<string> | null {
  try {
    const raw = window.localStorage.getItem(st.storeKey);
    if (raw === null) return null; // 저장한 적 없음 → 서버가 그린 기본값을 쓴다
    const arr = JSON.parse(raw);
    // ★ 배열이 아니면(옛 형식·다른 코드가 쓴 값) **없는 셈** 친다.
    //   빈 Set 으로 돌려주면 "전부 접힘" 으로 읽혀 트리가 통째로 접힌 채 뜨고,
    //   스스로 회복하지도 못한다.
    if (!Array.isArray(arr)) return null;
    return new Set(arr as string[]);
  } catch {
    return null; // 사파리 프라이빗 모드·깨진 값 — 없는 셈 친다
  }
}

function saveExpanded(st: TreeState, ids: Set<string>): void {
  try {
    window.localStorage.setItem(st.storeKey, JSON.stringify([...ids]));
  } catch {
    /* 저장 못 해도 화면은 동작한다 */
  }
}

/**
 * 이 줄의 펼침 버튼 라벨을 상태에 맞게 바꾼다.
 * 라벨이 고정이면 스크린리더가 접힌 줄에도 "접기" 라고 읽는다.
 */
function syncExpLabel(li: HTMLElement): void {
  const btn = li.querySelector<HTMLElement>(':scope > .nx-tree-row > .nx-tree-exp');
  if (!btn) return;
  const open = li.getAttribute('aria-expanded') !== 'false';
  const label = open ? btn.dataset.labelCollapse : btn.dataset.labelExpand;
  if (label) btn.setAttribute('aria-label', label);
  // aria-expanded 는 부모 li[role=treeitem] 몫이다 — 여기서 또 쓰면 값이 둘이 된다.
}

function applyExpanded(st: TreeState): void {
  const open = loadExpanded(st);
  // 빈 목록이 저장돼 있으면 그건 **전부 접은 상태**다 — 그대로 따른다.
  //
  // ★ 7차 검수(2차 통과) — 저장된 적이 없을 때 예전에는 **그냥 돌아갔다.**
  //   보통은 화면이 이미 서버 기본값(전부 펼침) 그대로라 문제가 없었는데,
  //   딱 한 경로에서 깨졌다: 검색 중 [모두 접기] 는 일부러 저장을 건너뛰므로
  //   (한때의 상태라서) 저장된 값이 여전히 없다 → [검색 해제] 를 눌러
  //   여기로 와도 **아무것도 되돌리지 않아** 트리가 접힌 채로 굳었다.
  //   새로고침 말고는 빠져나올 방법이 없었다.
  //   "저장된 적 없음" 은 곧 "서버가 그린 기본값" 이므로 여기서 그대로 복원한다.
  st.root.querySelectorAll<HTMLElement>('.nx-tree-node.has-children').forEach((li) => {
    const on = open === null ? true : open.has(li.dataset.id || '');
    li.setAttribute('aria-expanded', on ? 'true' : 'false');
    syncExpLabel(li);
  });
}

/** 지금 화면에 그려진 펼침 상태. 저장된 값이 없을 때의 출발점이 된다. */
function currentExpanded(st: TreeState): Set<string> {
  const ids = new Set<string>();
  st.root.querySelectorAll<HTMLElement>('.nx-tree-node.has-children').forEach((li) => {
    if (li.getAttribute('aria-expanded') !== 'false') ids.add(li.dataset.id || '');
  });
  return ids;
}

function setExpanded(st: TreeState, li: HTMLElement, on: boolean): void {
  li.setAttribute('aria-expanded', on ? 'true' : 'false');
  syncExpLabel(li);
  // 아직 저장한 적이 없으면 **지금 화면 상태**를 바탕으로 시작한다 —
  // 빈 Set 에서 시작하면 이번에 건드리지 않은 다른 가지들이 전부 접힌 것이 된다.
  const open = loadExpanded(st) ?? currentExpanded(st);
  const id = li.dataset.id || '';
  if (on) open.add(id);
  else open.delete(id);
  saveExpanded(st, open);
}

// ---------------------------------------------------------------- 툴박스
/**
 * 툴박스 팝업. `st` 를 주면 **그 트리가 가리키는** 것을, 안 주면 기본 것을.
 *
 * ★ 예전에는 `getElementById('nxtree-tool')` 로 못 박혀 있었다. 매크로는
 *   `toolbox(id=…)` 로 이름을 받는데 JS 가 무시하고 있어서, 화면에 트리가
 *   둘이면 두 번째 툴박스는 **영영 열리지 않는다.**
 */
function popFor(st?: TreeState): HTMLElement | null {
  return document.getElementById(st?.toolId || 'nxtree-tool');
}

/**
 * 지금 열려 있는 툴박스를 부른 버튼. 닫을 때 **초점을 여기로 되돌린다.**
 *
 * ★ 이게 없으면 메뉴를 Esc 로 닫는 순간 초점이 `<body>` 로 떨어진다.
 *   키보드만 쓰는 사람은 트리 맨 위부터 다시 Tab 을 눌러 내려와야 한다.
 */
let toolTrigger: HTMLElement | null = null;

/** 지금 열려 있는 툴박스 그 자체. 어느 트리의 것인지와 무관하게 이걸로 닫는다. */
let openPop: HTMLElement | null = null;

function openTool(st: TreeState, li: HTMLElement, trigger: HTMLElement | null): void {
  const pop = popFor(st);
  if (!pop) return;
  const node = readNode(li);
  activeId = node.id;

  // 이전에 열려 있던 버튼의 상태를 먼저 내린다(다른 줄의 ⋯ 를 바로 누른 경우).
  toolTrigger?.setAttribute('aria-expanded', 'false');
  toolTrigger = trigger;
  trigger?.setAttribute('aria-expanded', 'true');

  st.root.querySelectorAll('.nx-tree-node.is-active').forEach((n) =>
    n.classList.remove('is-active'),
  );
  li.classList.add('is-active');

  // 노드 종류에 맞는 버튼 세트만 보인다(원본의 wr_toolbtn_in.folder/node/root)
  const want = node.isGroup ? 'group' : 'node';
  pop.querySelectorAll<HTMLElement>('.nx-tree-pop-set').forEach((set) => {
    set.classList.toggle('is-on', set.dataset.for === want);
  });

  // 어느 줄의 메뉴인지 머리말에 적는다 — 없으면 팝업만 보고는 알 수 없다.
  const nameEl = pop.querySelector<HTMLElement>('[data-pop-name]');
  if (nameEl) nameEl.textContent = node.name;
  const iconEl = pop.querySelector<HTMLElement>('[data-pop-icon]');
  if (iconEl) iconEl.className = node.isGroup ? 'ri-folder-fill' : 'ri-file-text-line';

  // 깊이 상한에 걸리면 [하위 추가] 를 흐리게 — 숨기지 않는다.
  // 사라지면 버튼 위치가 매번 달라져 손이 기억하지 못한다.
  const atLimit = node.depth + 1 > st.maxDepth - 1;
  pop.querySelectorAll<HTMLElement>('[data-act$="_child"]').forEach((b) => {
    b.classList.toggle('is-off', atLimit);
  });

  // position:fixed 라 뷰포트 좌표를 그대로 쓴다(nx-tree.css 주석 참고).
  const row = li.querySelector('.nx-tree-row') as HTMLElement;
  const box = row.getBoundingClientRect();
  pop.hidden = false;
  const popW = pop.offsetWidth || 260;
  const popH = pop.offsetHeight || 48;
  // 아래로 열되, 화면 밖으로 나가면 위로 뒤집는다.
  const below = box.bottom + 4;
  pop.style.top =
    below + popH > window.innerHeight ? `${Math.max(4, box.top - popH - 4)}px` : `${below}px`;
  pop.style.left = `${Math.max(4, Math.min(box.right - popW, window.innerWidth - popW - 8))}px`;

  // ★ 초점을 메뉴 안으로 넣는다. `role="menu"` 를 붙여 놓고 초점을 안 옮기면
  //   보조기기는 메뉴가 열린 줄도 모르고, 키보드로는 항목에 닿을 방법이 없다.
  //   보이는 세트(node/group)의 첫 항목으로 간다.
  openPop = pop;
  errCtx = st;
  const firstItem = pop.querySelector<HTMLElement>(
    '.nx-tree-pop-set.is-on [role="menuitem"]',
  );
  (firstItem ?? pop).focus?.();
}

/**
 * 툴박스를 닫는다.
 *
 * @param restoreFocus 초점을 부른 버튼으로 되돌릴지. 사용자가 **바깥을 클릭**해서
 *   닫힌 경우에는 이미 그쪽으로 초점이 갔으므로 되돌리면 안 된다.
 */
function closeTool(restoreFocus = false): void {
  // ★ 8차 검수 — 열려 있는 팝업을 **id 로 찾지 않고 상태로 기억한다.**
  //   `popFor()` 를 인자 없이 부르면 늘 기본 툴박스만 본다. 트리가 둘이면
  //   두 번째 툴박스는 Esc·바깥클릭·스크롤 어느 것으로도 닫히지 않는다
  //   (그 상태의 스크롤 이탈이 곧 "다른 줄이 지워지는" 사고다).
  const pop = openPop ?? popFor();
  if (!pop || pop.hidden) return; // 이미 닫혀 있으면 초점을 건드리지 않는다
  pop.hidden = true;

  const trigger = toolTrigger;
  toolTrigger = null;
  openPop = null;
  trigger?.setAttribute('aria-expanded', 'false');
  if (restoreFocus && trigger && document.body.contains(trigger)) trigger.focus();
}

// ---------------------------------------------------------------- 서버 호출
async function reload(): Promise<void> {
  // 부분 갱신 대신 화면을 다시 받는다. 트리는 서버가 그리는 것이 진실이고,
  // 클라이언트에서 DOM 을 기워 맞추면 sort_order·depth 가 어긋날 여지가 생긴다.
  window.location.reload();
}

export const tree = {
  /** 현재 툴박스가 열려 있는 노드. */
  active(treeId?: string): TreeNode | null {
    const st = liveState(treeId);
    if (!st || !activeId) return null;
    const li = nodeEl(st.root, activeId);
    return li ? readNode(li) : null;
  },

  /** id 로 노드 하나. 화면에 없으면 null. */
  byId(id: string): TreeNode | null {
    for (const st of liveStates()) {
      const li = nodeEl(st.root, id);
      if (li) return readNode(li);
    }
    return null;
  },

  /** 직계 자식들. parentId 가 null 이면 최상위. */
  children(parentId: string | null, treeId?: string): TreeNode[] {
    const st = liveState(treeId);
    if (!st) return [];
    const sel = parentId
      ? `.nx-tree-node[data-parent="${CSS.escape(parentId)}"]`
      : '.nx-tree-node[data-parent=""]';
    return [...st.root.querySelectorAll<HTMLElement>(sel)].map(readNode);
  },

  /**
   * 전부 펼치기/접기.
   *
   * @param treeId 비우면 살아 있는 트리 전부. 검색줄은 **자기 트리만** 넘긴다.
   *
   * ★ 검색 중(`is-filtered`)에는 두 가지가 달라진다 —
   *   ① 매치의 조상은 접지 않는다. 접으면 결과가 전부 사라지는데 배지에는
   *     "3건" 이 그대로 남아, 화면과 숫자가 서로 다른 말을 한다.
   *   ② localStorage 에 저장하지 않는다. 검색 때의 펼침은 **한때의 상태**다.
   *     저장해 버리면 검색을 해제한 뒤에도 그 모양이 남는다.
   */
  expandAll(on = true, treeId?: string): void {
    const targets = liveStates().filter((st) => !treeId || st.root.id === treeId);
    for (const st of targets) {
      const filtered = st.root.classList.contains('is-filtered');
      const ids = new Set<string>();
      st.root.querySelectorAll<HTMLElement>('.nx-tree-node.has-children').forEach((li) => {
        // 검색 중에 접을 때, 매치를 품은 가지는 펼친 채로 둔다.
        const keepOpen =
          !on && filtered && (li.classList.contains('has-match') || li.classList.contains('is-match'));
        const open = keepOpen ? true : on;
        li.setAttribute('aria-expanded', open ? 'true' : 'false');
        syncExpLabel(li);
        if (open) ids.add(li.dataset.id || '');
      });
      if (!filtered) saveExpanded(st, ids);
    }
  },

  /** 이름·공통메모·표시상태 수정. **관리자만** 된다. */
  async update(id: string, patch: Record<string, unknown>): Promise<void> {
    await call(`${API}/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
  },

  /**
   * **내** 개인 메모 저장. 비우면 지워진다.
   *
   * ★ `update()` 와 엔드포인트가 다르다 — 이건 로그인한 사람이면 누구나 된다.
   *   서버가 저장 대상을 언제나 요청자 자신으로 고정하므로, 남의 메모를
   *   가리킬 방법이 없다.
   */
  async setNote(id: string, body: string): Promise<void> {
    await call(`${API}/${id}/note`, {
      method: 'PUT',
      body: JSON.stringify({ body }),
    });
  },

  /** 새 카테고리. position 을 비우면 형제 맨 뒤. */
  async add(opts: {
    parentId: string | null;
    name: string;
    isGroup: boolean;
    position?: number;
    memo?: string;
  }): Promise<string> {
    const r = await call(API, {
      method: 'POST',
      body: JSON.stringify({
        parent_id: opts.parentId,
        name: opts.name,
        is_group: opts.isGroup,
        position: opts.position,
        memo: opts.memo,
      }),
    });
    return String(r.id ?? '');
  },

  /** [상위 변경]. */
  async move(id: string, parentId: string | null, position?: number): Promise<void> {
    await call(`${API}/${id}/move`, {
      method: 'POST',
      body: JSON.stringify({ parent_id: parentId, position }),
    });
  },

  /** [위로]/[아래로]. 끝이면 false 를 돌려주고 아무 일도 하지 않는다. */
  async shift(id: string, delta: -1 | 1): Promise<boolean> {
    const r = await call(`${API}/${id}/shift`, {
      method: 'POST',
      body: JSON.stringify({ delta }),
    });
    return Boolean(r.moved);
  },

  /** 삭제. 기본은 promote — 하위가 부모 자리로 올라온다(원본과 같다). */
  async remove(
    id: string,
    strategy: 'promote' | 'block' | 'cascade' = 'promote',
  ): Promise<string[]> {
    const r = await call(`${API}/${id}?strategy=${strategy}`, { method: 'DELETE' });
    return (r.removed as string[] | undefined) ?? [];
  },

  /**
   * 검색 — 매치된 노드에 .is-match 를 붙이고 조상은 펼친다.
   *
   * 늦게 온 응답은 **버린다**(-1 을 돌려준다). 부르는 쪽은 -1 이면 화면을
   * 건드리지 않는다.
   */
  async search(q: string, scope = 'name', treeId?: string): Promise<number> {
    const st = liveState(treeId);
    if (!st) return 0;
    if (!q.trim()) {
      this.clearSearch(treeId);
      return 0;
    }
    const seq = ++st.searchSeq;
    const r = await call(
      `${API}/search?q=${encodeURIComponent(q)}&scope=${encodeURIComponent(scope)}`,
    );
    // ★ 7차 검수 — 예전에는 응답이 오면 **무조건** 화면을 고쳤다.
    //   느린 회선에서 Esc 로 검색을 취소한 뒤 응답이 도착하면, 빈 검색창에
    //   필터가 다시 걸리고 배지에 숫자가 떴다. 글자를 빠르게 치면 먼저 보낸
    //   요청이 나중에 도착해 **한 글자 전의 결과**가 화면에 남기도 했다.
    if (seq !== st.searchSeq) return -1;
    const ids = new Set<string>((r.ids as string[] | undefined) ?? []);

    st.root.classList.add('is-filtered');
    st.root.querySelectorAll<HTMLElement>('.nx-tree-node').forEach((li) => {
      li.classList.remove('is-match', 'has-match');
    });
    ids.forEach((id) => {
      let li = nodeEl(st.root, id);
      if (!li) return;
      li.classList.add('is-match');
      // 조상은 보이고 펼쳐져 있어야 매치가 화면에 나온다
      let parent = li.parentElement?.closest<HTMLElement>('.nx-tree-node') ?? null;
      while (parent) {
        parent.classList.add('has-match');
        parent.setAttribute('aria-expanded', 'true');
        syncExpLabel(parent); // 여기만 빠지면 펼쳐진 줄에 '펼치기' 라벨이 남는다
        parent = parent.parentElement?.closest<HTMLElement>('.nx-tree-node') ?? null;
      }
    });
    return ids.size;
  },

  /** 검색 해제 — 원래 펼침 상태로 되돌린다. `treeId` 를 주면 그 트리만. */
  clearSearch(treeId?: string): void {
    for (const st of liveStates()) {
      if (treeId && st.root.id !== treeId) continue;
      st.searchSeq += 1; // 날아가 있는 응답을 무효로 만든다
      st.root.classList.remove('is-filtered');
      st.root.querySelectorAll('.nx-tree-node').forEach((li) => {
        li.classList.remove('is-match', 'has-match');
      });
      applyExpanded(st);
    }
  },
};

// ---------------------------------------------------------------- 이벤트
function bindTree(root: HTMLElement): void {
  // ★ 두 번 붙으면 클릭 한 번에 두 번 반응한다. 팝업 조각은 나중에 들어오므로
  //   같은 요소에 다시 올 수 있다 — 표시를 남겨 한 번만 붙인다.
  if (root.dataset.nxBound === '1') return;
  root.dataset.nxBound = '1';

  const st: TreeState = {
    root,
    maxDepth: Number(root.dataset.maxDepth || 4),
    // ★ `data-scope` 로 한 겹 더 가른다. 문서마다 목차가 따로 있는데 키가
    //   같으면 **모든 문서가 하나의 펼침 상태를 공유한다**(그리고 /categories
    //   화면과도 겹친다). 지금은 비어 있어 예전 키와 같다.
    storeKey: `nx.tree.${document.body.dataset.slot || 'x'}.${
      root.dataset.scope ? `${root.dataset.scope}.` : ''
    }${root.id}.expanded`,
    searchSeq: 0,
    // 툴박스는 트리마다 다를 수 있다(_tree.html 의 toolbox(id=…)).
    toolId: root.dataset.tool || 'nxtree-tool',
  };
  states.set(root.id, st);
  applyExpanded(st);

  root.addEventListener('click', (ev) => {
    const btn = (ev.target as HTMLElement).closest<HTMLElement>('[data-act]');
    if (!btn || !root.contains(btn)) return;
    const li = btn.closest<HTMLElement>('.nx-tree-node');
    const act = btn.dataset.act;

    errCtx = st;
    if (act === 'toggle' && li) {
      setExpanded(st, li, li.getAttribute('aria-expanded') !== 'true');
      return;
    }
    if (act === 'open_tool' && li) {
      ev.stopPropagation();
      setRoving(st, li); // 마우스로 고른 줄이 곧 키보드의 현재 줄이 된다
      openTool(st, li, btn);
      return;
    }
    if (act === 'view_memo' && li) {
      const id = li.dataset.id;
      void window.nx?.modal?.open(`/categories/${id}/memo`, { size: 'sheet' });
    }
  });

  bindKeys(st);
  // 트리 전체가 Tab 한 번에 들어오게 첫 줄만 tabindex=0 으로 둔다(roving).
  const first = visibleRows(st)[0];
  if (first) setRoving(st, first);
}

// ---------------------------------------------------------------- 키보드
/**
 * `role="tree"` 의 키보드 규약(WAI-ARIA APG).
 *
 * ★ 왜 필요한가 — `role="tree"`/`treeitem` 을 붙여 놓으면 보조기기 사용자는
 *   **화살표로 오르내릴 수 있다고 안내받는다.** 그런데 실제로는 아무 반응이
 *   없었다(줄 자체가 초점을 받을 수 없었다). 역할만 적고 동작을 안 만들면
 *   역할을 아예 안 적은 것보다 나쁘다 — 없는 길을 알려 주는 셈이다.
 *
 * ★ roving tabindex — 줄마다 `tabindex=0` 을 주면 Tab 한 번에 한 줄씩,
 *   50줄 트리는 50번을 눌러야 지나간다. **딱 한 줄만** 0 이고 나머지는 -1 이라
 *   Tab 으로는 트리 전체가 한 정거장이고, 그 안은 화살표로 다닌다.
 *   줄 안의 버튼(+/−·메모·⋯)은 예전처럼 Tab 으로도 닿는다 — 화살표를 모르는
 *   사람의 길을 막지 않는다.
 *
 *   ← →  펼치기/접기(이미 그 상태면 자식/부모로 이동)
 *   ↑ ↓  보이는 줄을 따라 위/아래
 *   Home/End  첫 줄 / 마지막 줄
 *   Enter · F2   그 줄의 툴박스 열기(편집모드)
 */

/** 지금 화면에 **보이는** 줄들을 위에서 아래 순서로. 접힌 가지 안쪽과 검색이 감춘 줄은 뺀다. */
function visibleRows(st: TreeState): HTMLElement[] {
  const out: HTMLElement[] = [];
  const walk = (list: Element | null): void => {
    if (!list) return;
    for (const el of Array.from(list.children)) {
      if (!(el instanceof HTMLElement) || !el.classList.contains('nx-tree-node')) continue;
      // 검색 필터가 `display:none` 으로 감춘 줄은 offsetParent 가 없다.
      if (el.offsetParent === null) continue;
      out.push(el);
      if (el.getAttribute('aria-expanded') === 'false') continue; // 접혀 있으면 안쪽은 안 본다
      walk(el.querySelector(':scope > .nx-tree-list'));
    }
  };
  walk(st.root.querySelector(':scope > .nx-tree-list'));
  return out;
}

/** Tab 으로 들어올 줄을 한 개만 정한다. `focus=true` 면 초점까지 옮긴다. */
function setRoving(st: TreeState, li: HTMLElement, focus = false): void {
  st.root.querySelectorAll<HTMLElement>('.nx-tree-node').forEach((n) => {
    if (n !== li) n.tabIndex = -1;
  });
  li.tabIndex = 0;
  if (focus) li.focus();
}

function bindKeys(st: TreeState): void {
  st.root.addEventListener('keydown', (ev) => {
    const key = ev.key;
    if (
      key !== 'ArrowUp' &&
      key !== 'ArrowDown' &&
      key !== 'ArrowLeft' &&
      key !== 'ArrowRight' &&
      key !== 'Home' &&
      key !== 'End' &&
      key !== 'F2' &&
      key !== 'Enter'
    ) {
      return;
    }
    // 글자를 치는 칸(검색 트리 안의 라디오 등) 안에서는 화살표가 그쪽 몫이다.
    const target = ev.target as HTMLElement | null;
    if (target?.closest('input,select,textarea')) return;

    const li = target?.closest<HTMLElement>('.nx-tree-node');
    if (!li || !st.root.contains(li)) return;

    // Enter 는 버튼 위에서는 그 버튼의 것이다 — 가로채지 않는다.
    if (key === 'Enter' && target?.closest('button')) return;

    const rows = visibleRows(st);
    const i = rows.indexOf(li);

    const go = (next: HTMLElement | undefined | null): void => {
      // ★ 7차 검수 — 보이는지 확인한다. `ArrowRight` 는 `rows` 를 거치지 않고
      //   자식을 직접 집으므로, 검색으로 감춰진(`display:none`) 자식에 갈 수
      //   있었다. 그러면 `setRoving` 이 tabindex=0 을 그 줄로 옮기는데
      //   `focus()` 는 먹지 않아, **트리가 Tab 순서에서 통째로 빠진다.**
      if (!next || next.offsetParent === null) return;
      ev.preventDefault();
      setRoving(st, next, true);
    };

    switch (key) {
      case 'ArrowDown':
        go(rows[i + 1]);
        return;
      case 'ArrowUp':
        go(rows[i - 1]);
        return;
      case 'Home':
        go(rows[0]);
        return;
      case 'End':
        go(rows[rows.length - 1]);
        return;
      case 'ArrowRight':
        if (li.classList.contains('has-children')) {
          if (li.getAttribute('aria-expanded') === 'false') {
            ev.preventDefault();
            setExpanded(st, li, true);
            return;
          }
          // 이미 펼쳐져 있으면 첫 자식으로
          go(li.querySelector<HTMLElement>(':scope > .nx-tree-list > .nx-tree-node'));
        }
        return;
      case 'ArrowLeft':
        if (li.classList.contains('has-children') && li.getAttribute('aria-expanded') !== 'false') {
          ev.preventDefault();
          setExpanded(st, li, false);
          return;
        }
        go(li.parentElement?.closest<HTMLElement>('.nx-tree-node'));
        return;
      case 'Enter':
      case 'F2': {
        // 편집모드에서만 툴박스가 있다. 없으면 아무 일도 하지 않는다.
        const trigger = li.querySelector<HTMLElement>(
          ':scope > .nx-tree-row [data-act="open_tool"]',
        );
        if (!trigger) return;
        ev.preventDefault();
        openTool(st, li, trigger);
        return;
      }
    }
  });
}

/**
 * 툴박스를 바깥 클릭·Esc 로 닫는다.
 *
 * ★ `bindTree()` 안에 있으면 **트리마다** 붙는다. 팝업 안 선택 트리는 열 때마다
 *   새 DOM 이라 열 때마다 두 개씩 쌓이고, 닫힌 팝업의 DOM 을 붙잡아 둔다.
 *   이 둘은 트리 상태를 안 쓰므로 부팅 때 **한 번만** 붙이면 된다.
 */
function bindToolClose(): void {
  document.addEventListener('click', (ev) => {
    const pop = openPop;
    if (!pop || pop.hidden) return;
    const inside =
      pop.contains(ev.target as Node) ||
      (ev.target as HTMLElement).closest('[data-act="open_tool"]');
    // 바깥을 눌러 닫는 경우 초점은 이미 그쪽으로 갔다 — 되돌리지 않는다.
    if (!inside) closeTool();
  });

  // ★ 7차 검수 — 툴박스는 `position: fixed` 로 **한 번** 자리를 잡고 끝이다.
  //   트리 상자 안에서 휠을 굴리면 줄은 움직이는데 메뉴는 제자리에 남아,
  //   3번째 줄의 메뉴가 9번째 줄에 붙어 보인다. 그 상태에서 [삭제] 를 누르면
  //   `activeId` 는 여전히 3번째 줄이라 **화면에서 가리키던 것과 다른 줄이
  //   지워진다.** 되돌릴 수 없는 동작이므로 다시 자리를 잡는 대신 **닫는다** —
  //   자리를 옮기면 손이 따라가야 하지만, 닫히면 다시 누르면 그만이다.
  const closeOnMove = () => closeTool();
  // 스크롤은 버블링하지 않는다 → 캡처 단계로 받아야 트리 상자 안의 스크롤도 잡힌다.
  document.addEventListener('scroll', closeOnMove, true);
  window.addEventListener('resize', closeOnMove);

  document.addEventListener('keydown', (ev) => {
    const pop = openPop;
    if (!pop || pop.hidden) return;
    if (ev.key === 'Escape') {
      // Esc 로 닫을 때는 **부른 버튼으로 초점을 되돌린다.** 안 그러면
      // 초점이 <body> 로 떨어져 트리 맨 위부터 다시 Tab 을 눌러야 한다.
      ev.preventDefault();
      closeTool(true);
      return;
    }
    // 메뉴 안에서는 위/아래로 항목을 옮긴다(role="menu" 규약).
    if (ev.key !== 'ArrowDown' && ev.key !== 'ArrowUp') return;
    if (!pop.contains(ev.target as Node)) return;
    const items = [
      ...pop.querySelectorAll<HTMLElement>('.nx-tree-pop-set.is-on [role="menuitem"]'),
    ].filter((b) => !b.classList.contains('is-off'));
    if (!items.length) return;
    ev.preventDefault();
    const cur = items.indexOf(document.activeElement as HTMLElement);
    const step = ev.key === 'ArrowDown' ? 1 : -1;
    // 끝에서 반대편으로 감는다 — 목록이 짧아 되감기가 자연스럽다.
    items[(cur + step + items.length) % items.length].focus();
  });
}

function bindToolbox(pop: HTMLElement): void {
  // ★ 8차 검수 — 예전에는 `popFor()` 로 **기본 툴박스 하나**에만 붙였다.
  //   매크로는 `toolbox(id=…)` 로 여러 개를 그릴 수 있으므로, 두 번째 팝업의
  //   메뉴 항목은 전부 죽어 있었다. 화면에 있는 것 전부에 붙인다.
  if (pop.dataset.nxBound === '1') return;
  pop.dataset.nxBound = '1';

  pop.addEventListener('click', async (ev) => {
    const btn = (ev.target as HTMLElement).closest<HTMLElement>('[data-act]');
    if (!btn) return;
    const act = btn.dataset.act || '';
    const id = activeId;
    if (act === 'close_tool') {
      closeTool(true);
      return;
    }
    if (!id) return;
    const node = tree.byId(id);
    if (!node) return;

    // ★ **팝업을 열기 전에** 툴박스를 닫는다.
    //   예전에는 `finally` 에서만 닫았는데, 그건 `await modal.open(...)` 이
    //   **끝난 뒤**다. 그래서 모달이 뜬 동안 툴박스가 반투명 배경막 아래에
    //   그대로 떠 있었다(z-index 200 vs 998 이라 가려지긴 하지만 배경막 색이
    //   덧칠된 네모로 비쳤다). 지금 닫으면서 초점을 ⋯ 버튼으로 돌려 두면,
    //   이어서 열리는 모달이 그 버튼을 `prevFocus` 로 잡는다 —
    //   모달을 닫았을 때 초점이 **원래 그 줄로** 돌아온다.
    closeTool(true);

    const modal = window.nx?.modal;
    try {
      switch (act) {
        case 'edit':
        case 'edit_folder':
          if (await modal?.open(`/categories/${id}/edit`, { size: 'sheet' })) {
            await reload();
          }
          break;
        case 'memo':
          if (await modal?.open(`/categories/${id}/memo?edit=1`, { size: 'sheet' })) {
            await reload();
          }
          break;
        case 'move':
          if (await modal?.open(`/categories/${id}/move`, { size: 'sheet' })) {
            await reload();
          }
          break;
        case 'moveUp':
          if (await tree.shift(id, -1)) await reload();
          break;
        case 'moveDown':
          if (await tree.shift(id, 1)) await reload();
          break;
        case 'addfolder_next':
        case 'addnode_next':
        case 'addfolder_child':
        case 'addnode_child': {
          const asChild = act.endsWith('_child');
          const isGroup = act.startsWith('addfolder');
          const parent = asChild ? id : node.parentId || '';
          const url =
            `/categories/new?parent=${encodeURIComponent(parent)}` +
            `&is_group=${isGroup ? 'true' : 'false'}`;
          if (await modal?.open(url, { size: 'sheet' })) await reload();
          break;
        }
        case 'delete': {
          const r = await modal?.open(`/categories/${id}/delete`, { size: 'sheet' });
          if (r) await reload();
          break;
        }
      }
    } catch (e) {
      // ★ 브라우저 경고창을 쓰지 않는다. 같은 "로그인이 필요합니다" 가
      //   어떤 길로 왔느냐에 따라 팝업으로도 뜨고 경고창으로도 떴다.
      void window.nx?.modal?.alert((e as Error).message);
    } finally {
      closeTool();
    }
  });
}

/**
 * 트리 **바깥**의 버튼들 — [그룹 추가] · [항목 추가].
 *
 * ★ 이 버튼들은 박스 헤더에 있어서 `.nx-tree` 안쪽 리스너로는 닿지 않는다.
 *   (그래서 처음에 아무 반응이 없었다.) document 레벨에서 위임한다.
 */
function bindRootActions(): void {
  document.addEventListener('click', async (ev) => {
    const btn = (ev.target as HTMLElement).closest<HTMLElement>(
      '[data-act="addfolder_root"], [data-act="addnode_root"]',
    );
    if (!btn) return;
    // 트리 안의 툴박스 버튼은 bindToolbox 가 처리한다 — 여기서 두 번 잡지 않는다.
    if (btn.closest('.nx-tree-pop')) return;
    ev.preventDefault();

    const isGroup = btn.dataset.act === 'addfolder_root';
    const url = `/categories/new?parent=&is_group=${isGroup ? 'true' : 'false'}`;
    const modal = window.nx?.modal;
    try {
      if (modal) {
        if (await modal.open(url, { size: 'sheet' })) await reload();
      } else {
        // JS 팝업이 없으면 일반 페이지로 간다 — 기능이 죽지 않는다.
        window.location.href = url;
      }
    } catch (e) {
      void window.nx?.modal?.alert((e as Error).message);
    }
  });
}

function bindSearch(): void {
  document.querySelectorAll<HTMLElement>('.nx-tree-search').forEach((bar) => {
    const id = bar.dataset.tree || 'nxtree';
    const q = document.getElementById(`${id}-q`) as HTMLInputElement | null;
    const scope = document.getElementById(`${id}-scope`) as HTMLSelectElement | null;
    const count = document.getElementById(`${id}-count`);
    const clear = document.getElementById(`${id}-clear`);
    if (!q) return;

    // ★ 검색 중이면 트리가 "비어 보인다" — 아무것도 없는 것과 구분되게
    //   개수 뱃지와 [검색 해제] 를 함께 켠다.
    const setFiltering = (on: boolean, n = 0) => {
      if (count) {
        count.textContent = String(n);
        count.hidden = !on;
      }
      if (clear) clear.hidden = !on;
    };

    const reset = () => {
      q.value = '';
      tree.clearSearch(id);
      setFiltering(false);
    };

    const fire = async () => {
      if (!q.value.trim()) {
        reset();
        return;
      }
      try {
        const n = await tree.search(q.value, scope?.value || 'name', id);
        // -1 = 늦게 온 응답이라 트리를 건드리지 않았다 → 배지도 그대로 둔다.
        if (n >= 0) setFiltering(true, n);
      } catch {
        /* 검색 실패는 조용히 — 트리는 그대로 보인다 */
      }
    };

    let timer = 0;
    const run = () => {
      window.clearTimeout(timer);
      // 300ms 디바운스 — 글자마다 서버를 때리지 않는다
      timer = window.setTimeout(fire, 300);
    };

    // [검색] 아이콘과 Enter 는 디바운스를 기다리지 않는다 — 누른 즉시 간다
    const now = () => {
      window.clearTimeout(timer);
      void fire();
    };

    q.addEventListener('input', run);
    q.addEventListener('keydown', (ev) => {
      const key = (ev as KeyboardEvent).key;
      if (key === 'Escape') reset();
      if (key === 'Enter') {
        ev.preventDefault();
        now();
      }
    });
    scope?.addEventListener('change', run);

    bar.querySelectorAll<HTMLElement>('[data-act]').forEach((b) => {
      b.addEventListener('click', () => {
        // ★ 7차 검수 — 이 검색줄이 맡은 트리 **하나만** 접고 편다.
        //   예전에는 살아 있든 죽었든 등록된 트리 전부에 걸려, 본 화면의
        //   [모두 접기] 가 팝업 안 선택 트리의 저장값까지 뒤집었다.
        if (b.dataset.act === 'expand_all') tree.expandAll(true, id);
        if (b.dataset.act === 'collapse_all') tree.expandAll(false, id);
        if (b.dataset.act === 'do_search') now();
        if (b.dataset.act === 'clear_search') reset();
      });
    });
  });
}

/**
 * 선택 트리(`.is-picker`)에서 **지금 고른 부모**를 읽는다.
 *
 * 돌려주는 값
 *   `{ id }`      골랐다. `id === null` 이면 "최상위".
 *   `undefined`   **아무것도 안 골랐다.**
 *
 * ★ 7차 검수 — 예전에는 이 셋을 구분하지 않고
 *     `picked?.dataset.id ?? (radio ? radio.value || null : ...)`
 *   처럼 한 줄로 뭉갰다. 아무것도 안 고른 상태가 조용히 `null`(= 최상위)로
 *   내려앉아, [상위 변경] 팝업을 열고 **아무것도 안 고른 채 확인을 누르면
 *   가지째 최상위로 튀어나갔다.** 확인 버튼의 글자가 팝업 제목과 같은
 *   "상위 변경" 이라 사람은 그걸 "확인" 으로 읽는다.
 */
function pickedParent(box: HTMLElement): { id: string | null } | undefined {
  const node = box.querySelector<HTMLElement>('.nx-tree-node.is-picked');
  if (node) return { id: node.dataset.id || null };
  const radio = box.querySelector<HTMLInputElement>('input[name="new_parent"]:checked');
  if (radio) return { id: radio.value || null };
  return undefined;
}

/**
 * 라디오("최상위")와 트리 선택을 **양방향으로** 묶는다.
 *
 * ★ 7차 검수 — 한쪽 방향만 있었다(트리를 고르면 라디오를 껐다). 반대로
 *   라디오를 다시 켜도 `is-picked` 가 남았고, 읽는 쪽은 `is-picked` 를
 *   먼저 보므로 **화면에는 "최상위" 가 켜져 있는데 실제로는 그 노드 밑에**
 *   만들어졌다. 화면에 선택이 두 개로 보이는 것도 같은 원인이다.
 */
function bindPickerRadio(): void {
  document.addEventListener('change', (ev) => {
    const radio = (ev.target as HTMLElement | null)?.closest<HTMLInputElement>(
      'input[name="new_parent"]',
    );
    if (!radio || !radio.checked) return;
    // 같은 상자 안의 트리만 푼다 — 화면에 선택 트리가 둘일 수 있다.
    const scope = radio.closest<HTMLElement>('[data-id], form') ?? document;
    scope.querySelectorAll('.nx-tree-node.is-picked').forEach((n) =>
      n.classList.remove('is-picked'),
    );
  });
}

/**
 * 팝업 안의 [저장] 버튼들.
 *
 * 팝업은 나중에 만들어지는 별도 DOM 이라 document 레벨에서 위임한다.
 * 저장에 성공하면 `nx.modal.done()` 으로 부모에게 값을 넘긴다 —
 * **[저장]만 값을 전달한다**는 nxModal 규약(SNAPSHOT 10차)을 그대로 따른다.
 * 실패하면 팝업을 닫지 않고 오류 문구만 바꾼다.
 */
function bindForms(): void {
  bindPickerRadio();
  const showErr = (el: HTMLElement | null, msg: string): void => {
    if (!el) {
      // 오류를 적을 자리가 없는 화면 — 경고창 대신 같은 모양의 팝업으로.
      void window.nx?.modal?.alert(msg);
      return;
    }
    el.textContent = msg;
    el.hidden = false;
  };

  // ★ Enter 로 제출되는 경우 — 입력칸이 하나뿐인 폼은 브라우저가 **암묵적으로**
  //   제출한다. 이 폼들은 `action` 이 없으므로 그대로 두면 지금 URL(= GET 전용
  //   화면)로 POST 가 나가 405 가 된다. Enter 는 [저장] 을 누른 것으로 바꾼다.
  const ENTER_SAVE: Record<string, string> = {
    'nx-cat-form': 'save_cat',
    'nx-cat-memo': 'save_memo',
  };
  document.addEventListener('submit', (ev) => {
    const form = (ev.target as HTMLElement | null)?.closest<HTMLFormElement>('form');
    const act = form ? ENTER_SAVE[form.id] : undefined;
    if (!act) return;
    ev.preventDefault();
    document.querySelector<HTMLElement>(`[data-act="${act}"]`)?.click();
  });

  document.addEventListener('click', async (ev) => {
    const btn = (ev.target as HTMLElement).closest<HTMLElement>('[data-act]');
    if (!btn) return;
    const act = btn.dataset.act || '';
    const modal = window.nx?.modal;

    try {
      // ── 이름·표시상태 (생성 겸용)
      if (act === 'save_cat') {
        const form = document.getElementById('nx-cat-form') as HTMLElement | null;
        if (!form) return;
        const err = document.getElementById('nx-cat-err');
        const name = (form.querySelector('#cat-name') as HTMLInputElement)?.value ?? '';
        if (!name.trim()) {
          showErr(err, errText('tree.error.name_required'));
          return;
        }
        const hidden = (form.querySelector('#cat-status') as HTMLInputElement)?.checked;

        if (form.dataset.mode === 'create') {
          const pos = form.dataset.position;
          // 부모는 폼의 선택 트리에서 읽는다 — 트리에서 고른 것이 우선,
          // 그다음이 "최상위" 라디오, 둘 다 없으면 **불러온 자리 그대로**.
          const box = document.getElementById('nx-cat-parent');
          const choice = box ? pickedParent(box) : undefined;
          const parentId = choice ? choice.id : form.dataset.parent || null;
          await tree.add({
            parentId: parentId || null,
            name,
            isGroup: form.dataset.group === '1',
            position: pos ? Number(pos) : undefined,
          });
        } else {
          const patch: Record<string, unknown> = {};
          // ★ 8차 검수 — 이름을 **늘** 보내고 있었다. 그런데 입력칸에 들어 있는
          //   글자는 다른 언어에서 온 **폴백**일 수 있다. 그러면 "숨김" 체크만
          //   건드려도 그 폴백 글자가 지금 언어의 진짜 번역으로 저장되어,
          //   원본을 고쳐도 이쪽에는 영영 반영되지 않는다(조용한 번역 분기).
          //   손대지 않았고 + 폴백이면 보내지 않는다.
          const nameEl = form.querySelector<HTMLInputElement>('#cat-name');
          const untouched = nameEl ? nameEl.value === (nameEl.dataset.original ?? '') : false;
          const isFallback = Boolean(
            nameEl?.dataset.langUsed && nameEl.dataset.langUsed !== (window.nx?.lang ?? ''),
          );
          if (!(untouched && isFallback)) patch.name = name;
          if (hidden !== undefined) patch.status = hidden ? 9 : 1;
          await tree.update(form.dataset.id || '', patch);
        }
        modal?.done({ ok: true });
        return;
      }

      // ── 메모 — **두 종류**를 각자의 권한으로 저장한다.
      //    공통 메모  PATCH /api/categories/{id}   (관리자만)
      //    개인 메모  PUT   /api/categories/{id}/note (로그인한 사람 누구나)
      //    한 요청으로 합치지 않는다 — 권한이 다르고, 일반 사용자가
      //    공통 메모까지 보내면 그 요청 전체가 403 이 되어 **자기 메모도
      //    저장되지 않는다.**
      if (act === 'save_memo') {
        const form = document.getElementById('nx-cat-memo') as HTMLElement | null;
        if (!form) return;
        const id = form.dataset.id || '';
        const noteEl = form.querySelector<HTMLTextAreaElement>('#cat-note');
        const memoEl = form.querySelector<HTMLTextAreaElement>('#cat-memo');

        // 공통 메모는 입력칸이 **있을 때만** 보낸다(관리자가 아니면 읽기로 뜬다).
        if (form.dataset.canShared === '1' && memoEl) {
          await tree.update(id, { memo: memoEl.value });
        }
        if (noteEl) {
          await tree.setNote(id, noteEl.value);
        }
        modal?.done({ ok: true });
        return;
      }

      // ── 상위 변경
      if (act === 'save_move') {
        const box = document.getElementById('nx-cat-move') as HTMLElement | null;
        if (!box) return;
        const choice = pickedParent(box);
        // ★ 아무것도 안 골랐으면 **아무 일도 하지 않는다.**
        //   예전에는 조용히 최상위로 옮겼다(서버는 그걸 정상 요청으로 받는다).
        //   서버가 지금 부모를 미리 골라 주므로(categories.py 의 picked)
        //   여기까지 오는 경우는 사람이 선택을 지운 경우뿐이다.
        if (!choice) {
          showErr(document.getElementById('nx-move-err'), errText('tree.error.move_no_change'));
          return;
        }
        // ★ 지금 자리와 같으면 서버를 부르지 않는다.
        //   같은 부모로 move 하면 `position` 이 비어 있어 **형제 맨 뒤로** 간다 —
        //   "취소" 하려던 사람이 순서를 바꾸게 된다.
        const now = box.dataset.currentParent || '';
        if ((choice.id || '') === now) {
          showErr(document.getElementById('nx-move-err'), errText('tree.error.move_no_change'));
          return;
        }
        await tree.move(box.dataset.id || '', choice.id);
        modal?.done({ ok: true });
        return;
      }

      // ── 삭제
      if (act === 'save_delete') {
        const box = document.getElementById('nx-cat-del') as HTMLElement | null;
        if (!box) return;
        const s = box.querySelector<HTMLInputElement>('[name="strategy"]:checked')
          ?? box.querySelector<HTMLInputElement>('[name="strategy"]');
        await tree.remove(
          box.dataset.id || '',
          (s?.value as 'promote' | 'cascade') || 'promote',
        );
        modal?.done({ ok: true });
        return;
      }
    } catch (e) {
      const err =
        document.getElementById('nx-cat-err') ??
        document.getElementById('nx-memo-err') ??
        document.getElementById('nx-move-err') ??
        document.getElementById('nx-del-err');
      showErr(err, (e as Error).message);
    }
  });

  // 선택 전용 트리 — 제목을 누르면 그 자리가 선택된다(라디오 대신).
  document.addEventListener('click', (ev) => {
    const title = (ev.target as HTMLElement).closest<HTMLElement>(
      '.nx-tree.is-picker .nx-tree-title',
    );
    if (!title) return;
    const box = title.closest<HTMLElement>('.nx-tree');
    const li = title.closest<HTMLElement>('.nx-tree-node');
    if (!box || !li || li.classList.contains('is-blocked')) return;
    box.querySelectorAll('.nx-tree-node.is-picked').forEach((n) =>
      n.classList.remove('is-picked'),
    );
    li.classList.add('is-picked');
    // "최상위" 라디오는 해제한다 — 둘 중 하나만 선택된다.
    // ★ 7차 검수 — `document.querySelector` 였다. 화면 어딘가의 **다른** 라디오를
    //   집을 수 있었다(읽는 쪽은 `#nx-cat-move` / `#nx-cat-parent` 안으로 범위를
    //   좁혀 읽는데, 끄는 쪽만 문서 전체였다). 같은 상자 안에서 찾는다.
    const scope = box.closest<HTMLElement>('[data-id], form') ?? document;
    scope.querySelectorAll<HTMLInputElement>('input[name="new_parent"]').forEach((r) => {
      r.checked = false;
    });
  });
}

/** 화면에 있는(또는 새로 들어온) 트리를 전부 배선한다. 이미 붙은 것은 건너뛴다. */
function bindTreesIn(root: ParentNode): void {
  root
    .querySelectorAll<HTMLElement>('.nx-tree[data-max-depth]')
    .forEach(bindTree);
}

export function initTree(): void {
  // 팝업 폼은 트리가 없는 화면에서도 열릴 수 있으므로 항상 붙인다(리스너 몇 개).
  bindForms();
  bindRootActions();
  bindToolClose();

  // ★ 팝업 안의 선택 트리([상위 변경]·[어디에 만들까요?])는 **나중에** DOM 에
  //   들어온다. 부팅 때 한 번만 훑으면 그 트리들은 영영 배선되지 않아
  //   +/− 접기 버튼이 죽는다(항목 고르기는 document 리스너라 되고 있었다).
  //   `modal.ts` 가 조각을 끼운 뒤 알려주면 그때 붙인다.
  document.addEventListener('nx:modal:load', (ev) => {
    const root = (ev as CustomEvent<{ root?: ParentNode }>).detail?.root;
    bindTreesIn(root ?? document);
    (root ?? document)
      .querySelectorAll<HTMLElement>('.nx-tree-pop')
      .forEach(bindToolbox);
  });

  const roots = document.querySelectorAll<HTMLElement>('.nx-tree[data-max-depth]');
  if (!roots.length) return;
  roots.forEach(bindTree);
  // 화면에 있는 툴박스 **전부**에 붙인다(트리가 둘이면 팝업도 둘이다).
  document.querySelectorAll<HTMLElement>('.nx-tree-pop').forEach(bindToolbox);
  bindSearch();
}
